import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, GraduationCap, Users, Scale, Sparkles,
  Building2, Briefcase, Stethoscope, Trophy, Globe2,
  Mail, Phone, MapPin, CheckCircle, ExternalLink, Package
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SponsorshipPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [packages, setPackages] = useState([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(true);

  // Fetch sponsorship packages from CMS
  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/cms/packages`);
        if (response.ok) {
          const data = await response.json();
          setPackages(data);
        }
      } catch (err) {
        console.error('Failed to fetch packages:', err);
      } finally {
        setIsLoadingPackages(false);
      }
    };
    fetchPackages();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission - opens email client
    const subject = encodeURIComponent('Partnership Inquiry - Quantum Housing Conference');
    const body = encodeURIComponent(
      `Name: ${formData.name}\nOrganization: ${formData.organization}\nEmail: ${formData.email}\nPhone: ${formData.phone}\n\nMessage:\n${formData.message}`
    );
    window.location.href = `mailto:contact@globalintech.ai?subject=${subject}&body=${body}`;
    
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 1000);
  };

  // The three pillars of partnership value
  const valuePropositions = [
    {
      title: 'The Venue',
      subtitle: 'Harvard University',
      icon: GraduationCap,
      image: 'https://images.unsplash.com/photo-1767410913219-2db49c0575be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxIYXJ2YXJkJTIwVW5pdmVyc2l0eSUyMGNhbXB1cyUyMGhpc3RvcmljJTIwYnVpbGRpbmdzfGVufDB8fHx8MTc2OTc2NzA0OHww&ixlib=rb-4.1.0&q=85',
      description: "This isn't just a conference—it's a gathering at the world's most prestigious academic institution. Harvard's Tsai Auditorium has hosted Nobel laureates, heads of state, and industry titans. Your brand will stand alongside centuries of intellectual legacy.",
      highlights: [
        'Tsai Auditorium, CGIS South Building',
        'Exclusive MIT Media Lab, City Science Tour',
        'Private networking in historic halls'
      ]
    },
    {
      title: 'The Audience',
      subtitle: 'A Rare Convergence',
      icon: Users,
      image: 'https://images.unsplash.com/photo-1719085202281-688805459948?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHF1YW50dW0lMjB0ZWNobm9sb2d5JTIwbmV0d29ya3xlbnwwfHx8fDE3Njk3NjcwNTB8MA&ixlib=rb-4.1.0&q=85',
      description: "We're curating 160 of the most influential minds in housing, policy, and capital deployment. This is not a public event—it's an invitation-only summit where real decisions are made and partnerships are forged.",
      highlights: [
        'High-net-worth individuals & family offices',
        'C-suite executives from Fortune 500 firms',
        'Global policy advisors & legislators'
      ]
    },
    {
      title: 'The Impact',
      subtitle: 'Be a Founding Partner',
      icon: Globe2,
      image: 'https://images.unsplash.com/photo-1733846559897-24709ae35c3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxIYXJ2YXJkJTIwVW5pdmVyc2l0eSUyMGNhbXB1cyUyMGhpc3RvcmljJTIwYnVpbGRpbmdzfGVufDB8fHx8MTc2OTc2NzA0OHww&ixlib=rb-4.1.0&q=85',
      description: "The Quantum Housing initiative represents a paradigm shift—using athlete taxation to fund affordable housing globally. Partners joining now will be recognized as founding architects of a movement that could house 96,000 families daily.",
      highlights: [
        'Shape the future of housing policy',
        'First-mover brand association',
        'Legacy recognition in perpetuity'
      ]
    }
  ];

  // Audience segments
  const audienceSegments = [
    {
      icon: Trophy,
      title: 'Professional Athletes',
      description: 'NFL & NBA stars seeking meaningful capital deployment and legacy-building opportunities beyond the game.'
    },
    {
      icon: Stethoscope,
      title: 'Academics & Medical Leaders',
      description: 'PhDs and medical experts driving research on the intersection of housing stability and health equity.'
    },
    {
      icon: Scale,
      title: 'Policymakers',
      description: 'State legislators, city planners, and federal advisors shaping the next decade of urban development law.'
    },
    {
      icon: Briefcase,
      title: 'Impact Investors',
      description: 'Family offices, foundations, and ESG-focused funds with $500M+ in deployable capital.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Full Bleed with Dual Image */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden" data-testid="partnership-hero">
        {/* Background - Split Design */}
        <div className="absolute inset-0 z-0">
          {/* Harvard Image - Left Half */}
          <div className="absolute inset-0 w-full">
            <img 
              src="https://images.unsplash.com/photo-1743653773059-bd73c9230534?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwzfHxIYXJ2YXJkJTIwVW5pdmVyc2l0eSUyMGNhbXB1cyUyMGhpc3RvcmljJTIwYnVpbGRpbmdzfGVufDB8fHx8MTc2OTc2NzA0OHww&ixlib=rb-4.1.0&q=85"
              alt="Harvard University"
              className="w-full h-full object-cover"
            />
          </div>
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/50" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-24 pt-24 text-center">
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700"
          >
            <Sparkles className="w-4 h-4 text-secondary" />
            <span className="text-sm font-medium">An Invitation to Shape History</span>
          </div>

          <h1 
            className="font-outfit text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100"
            data-testid="partnership-title"
          >
            The Harvard
            <br />
            <span className="text-gradient-quantum">Opportunity</span>
          </h1>

          <p 
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200"
          >
            This is not a sponsorship. It's a seat at the table where the future of global housing 
            policy is being written. Limited partnerships available for organizations ready to lead.
          </p>

          <div 
            className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-300"
          >
            <a
              href="#contact-form"
              className="group flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all duration-300 hover:-translate-y-1 glow-primary"
              data-testid="request-deck-btn"
            >
              Request Partnership Deck
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="mailto:vernand@globalintech.ai?subject=Partnership Discussion - Quantum Housing Conference"
              className="flex items-center gap-2 px-8 py-4 glass-card rounded-full font-semibold text-lg transition-all duration-300 hover:bg-white/10"
              data-testid="contact-chair-btn"
            >
              <Mail className="w-5 h-5" />
              Contact the Chair
            </a>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-muted-foreground/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-primary rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="why-partner-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Why Partner With Us
            </span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold tracking-tight">
              Three Pillars of <span className="text-gradient-quantum">Extraordinary Value</span>
            </h2>
          </div>

          <div className="space-y-24">
            {valuePropositions.map((prop, index) => (
              <div 
                key={index}
                className={`grid md:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'md:grid-flow-dense' : ''
                }`}
                data-testid={`value-prop-${index}`}
              >
                {/* Image Side */}
                <div className={`relative ${index % 2 === 1 ? 'md:col-start-2' : ''}`}>
                  <div className="aspect-[4/3] rounded-2xl overflow-hidden glass-card">
                    <img 
                      src={prop.image}
                      alt={prop.title}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>
                  {/* Floating Badge */}
                  <div className="absolute -bottom-4 -right-4 glass-card rounded-xl p-4 hidden md:block">
                    <prop.icon className="w-8 h-8 text-primary" />
                  </div>
                </div>

                {/* Content Side */}
                <div className={index % 2 === 1 ? 'md:col-start-1 md:row-start-1' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <prop.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-secondary font-medium">{prop.subtitle}</p>
                      <h3 className="font-outfit text-2xl md:text-3xl font-bold">{prop.title}</h3>
                    </div>
                  </div>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                    {prop.description}
                  </p>

                  <ul className="space-y-3">
                    {prop.highlights.map((highlight, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="font-medium">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Audience Section */}
      <section className="py-24 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-transparent via-primary/5 to-transparent" data-testid="audience-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
              Who Will Be In The Room
            </span>
            <h2 className="font-outfit text-3xl md:text-5xl font-bold tracking-tight mb-4">
              A <span className="text-gradient-housing">Rare Convergence</span> of Influence
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              160 invitation-only attendees. No general admission. Every seat is occupied by a decision-maker.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {audienceSegments.map((segment, index) => (
              <div 
                key={index}
                className="glass-card rounded-2xl p-8 text-center transition-all duration-300 hover:-translate-y-2 hover:border-primary/50 group"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                  <segment.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-outfit text-xl font-bold mb-3">{segment.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{segment.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship Packages Section - Only shows if packages exist in CMS */}
      {packages.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="packages-section">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
                Partnership Tiers
              </span>
              <h2 className="font-outfit text-3xl md:text-5xl font-bold tracking-tight">
                Sponsorship <span className="text-gradient-quantum">Packages</span>
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                Each tier offers unique visibility and engagement opportunities. Contact us for custom packages.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map((pkg, index) => {
                // Tier-specific styling
                const tierStyles = {
                  0: { gradient: 'from-amber-500/20 via-amber-600/10 to-transparent', border: 'border-amber-500/50', icon: '👑', glow: 'hover:shadow-[0_0_40px_rgba(245,158,11,0.2)]' },
                  1: { gradient: 'from-slate-300/20 via-slate-400/10 to-transparent', border: 'border-slate-400/50', icon: '💎', glow: 'hover:shadow-[0_0_40px_rgba(148,163,184,0.2)]' },
                  2: { gradient: 'from-yellow-500/20 via-yellow-600/10 to-transparent', border: 'border-yellow-500/50', icon: '🏆', glow: 'hover:shadow-[0_0_40px_rgba(234,179,8,0.2)]' },
                  3: { gradient: 'from-gray-400/20 via-gray-500/10 to-transparent', border: 'border-gray-400/50', icon: '🥈', glow: 'hover:shadow-[0_0_40px_rgba(156,163,175,0.2)]' }
                };
                const style = tierStyles[index] || tierStyles[3];
                
                return (
                  <div 
                    key={pkg.id}
                    className={`relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 ${style.glow} ${
                      pkg.is_featured ? 'ring-2 ring-primary lg:scale-105 z-10' : ''
                    }`}
                  >
                    {/* Background gradient */}
                    <div className={`absolute inset-0 bg-gradient-to-b ${style.gradient}`} />
                    
                    {/* Glass card */}
                    <div className={`relative glass-card rounded-2xl p-6 h-full border ${style.border} flex flex-col`}>
                      {/* Badges */}
                      <div className="absolute -top-0 left-0 right-0 flex justify-center gap-2 -translate-y-1/2">
                        {pkg.is_featured && (
                          <span className="px-4 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-full shadow-lg">
                            Most Popular
                          </span>
                        )}
                        {pkg.is_exclusive && (
                          <span className="px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xs font-bold rounded-full shadow-lg">
                            Exclusive
                          </span>
                        )}
                      </div>
                      
                      {/* Header */}
                      <div className="text-center pt-4 mb-4">
                        <span className="text-3xl mb-2 block">{style.icon}</span>
                        <h3 className="font-outfit text-xl font-bold">{pkg.name}</h3>
                        {pkg.tagline && <p className="text-sm text-muted-foreground mt-1">{pkg.tagline}</p>}
                      </div>
                      
                      {/* Price */}
                      <div className="text-center mb-4">
                        <div className="font-outfit text-3xl md:text-4xl font-bold text-gradient-quantum">
                          {pkg.price_display || `$${pkg.price.toLocaleString()}`}
                        </div>
                        {pkg.limit && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Limited to {pkg.limit} {pkg.limit === 1 ? 'sponsor' : 'sponsors'}
                          </p>
                        )}
                      </div>
                      
                      {/* Description */}
                      {pkg.description && (
                        <p className="text-muted-foreground text-sm text-center mb-4 leading-relaxed">
                          {pkg.description}
                        </p>
                      )}
                      
                      {/* Benefits */}
                      {pkg.benefits?.length > 0 && (
                        <ul className="space-y-2.5 mb-6 flex-1">
                          {pkg.benefits.map((benefit, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-sm">
                              <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                              <span className="text-foreground/90">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      
                      {/* CTA Button */}
                      <a
                        href="#contact-form"
                        className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold transition-all duration-300 hover:-translate-y-0.5 mt-auto ${
                          pkg.is_featured 
                            ? 'bg-primary text-primary-foreground hover:shadow-lg hover:shadow-primary/25' 
                            : 'glass-card border border-border hover:border-primary/50 hover:bg-white/5'
                        }`}
                      >
                        Request Info
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Custom Package Note */}
            <div className="mt-12 text-center">
              <p className="text-muted-foreground mb-4">
                Looking for a custom partnership structure?
              </p>
              <a
                href="mailto:vernand@globalintech.ai?subject=Custom Partnership Inquiry"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                Contact us for tailored solutions
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Detailed Tier Comparison Grid */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
              Partnership Benefits Comparison
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Detailed breakdown of what each sponsorship tier includes
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="w-full glass-card rounded-2xl overflow-hidden">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left p-4 font-semibold">Benefits</th>
                  <th className="text-center p-4 font-semibold bg-secondary/10">
                    <span className="text-secondary">Presenting</span>
                    <div className="text-xs text-muted-foreground font-normal mt-1">$100,000</div>
                  </th>
                  <th className="text-center p-4 font-semibold">
                    Headline
                    <div className="text-xs text-muted-foreground font-normal mt-1">$50,000</div>
                  </th>
                  <th className="text-center p-4 font-semibold">
                    Supporting
                    <div className="text-xs text-muted-foreground font-normal mt-1">$25,000</div>
                  </th>
                  <th className="text-center p-4 font-semibold">
                    Contributing
                    <div className="text-xs text-muted-foreground font-normal mt-1">$10,000</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {/* Branding & Recognition */}
                <tr className="bg-primary/5">
                  <td colSpan="5" className="p-3 font-semibold text-primary">Branding & Recognition</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Logo on conference materials</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Website logo placement</td>
                  <td className="text-center p-4 text-sm text-secondary font-medium">Hero Banner</td>
                  <td className="text-center p-4 text-sm">Premium</td>
                  <td className="text-center p-4 text-sm">Standard</td>
                  <td className="text-center p-4 text-sm">Listed</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Social media recognition</td>
                  <td className="text-center p-4 text-sm">Dedicated posts</td>
                  <td className="text-center p-4 text-sm">Featured</td>
                  <td className="text-center p-4 text-sm">Mentioned</td>
                  <td className="text-center p-4 text-sm">Tagged</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Step & Repeat banner logo</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                
                {/* Speaking & Content */}
                <tr className="bg-primary/5">
                  <td colSpan="5" className="p-3 font-semibold text-primary">Speaking & Content</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Keynote speaking slot</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Panel participation</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Lightning talk (5 min)</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>

                {/* Event Access */}
                <tr className="bg-primary/5">
                  <td colSpan="5" className="p-3 font-semibold text-primary">Event Access</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Conference passes</td>
                  <td className="text-center p-4 text-sm font-medium">10</td>
                  <td className="text-center p-4 text-sm">6</td>
                  <td className="text-center p-4 text-sm">4</td>
                  <td className="text-center p-4 text-sm">2</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Chairman's Dinner seats</td>
                  <td className="text-center p-4 text-sm font-medium">6</td>
                  <td className="text-center p-4 text-sm">4</td>
                  <td className="text-center p-4 text-sm">2</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">MIT Media Lab, City Science Tour access</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">VIP networking breakfast</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>

                {/* Networking & Engagement */}
                <tr className="bg-primary/5">
                  <td colSpan="5" className="p-3 font-semibold text-primary">Networking & Engagement</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Private meeting room</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Exhibition space</td>
                  <td className="text-center p-4 text-sm font-medium">Premium booth</td>
                  <td className="text-center p-4 text-sm">Standard booth</td>
                  <td className="text-center p-4 text-sm">Table display</td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Attendee list access</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>

                {/* Digital & Media */}
                <tr className="bg-primary/5">
                  <td colSpan="5" className="p-3 font-semibold text-primary">Digital & Media</td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Conference recording access</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                </tr>
                <tr className="hover:bg-white/5">
                  <td className="p-4 text-sm">Post-event report mention</td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4"><CheckCircle className="w-5 h-5 text-green-500 mx-auto" /></td>
                  <td className="text-center p-4 text-muted-foreground">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-8 text-center">
            <a
              href="#contact-form"
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-full font-semibold transition-all hover:-translate-y-1"
            >
              Become a Sponsor <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto">
          <div className="glass-card rounded-3xl p-8 md:p-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="font-outfit text-4xl md:text-5xl font-bold text-gradient-quantum mb-2">160</div>
                <div className="text-sm text-muted-foreground">VIP Attendees</div>
              </div>
              <div>
                <div className="font-outfit text-4xl md:text-5xl font-bold text-gradient-quantum mb-2">12</div>
                <div className="text-sm text-muted-foreground">Expert Speakers</div>
              </div>
              <div>
                <div className="font-outfit text-4xl md:text-5xl font-bold text-gradient-quantum mb-2">$2B+</div>
                <div className="text-sm text-muted-foreground">Capital Represented</div>
              </div>
              <div>
                <div className="font-outfit text-4xl md:text-5xl font-bold text-gradient-quantum mb-2">1</div>
                <div className="text-sm text-muted-foreground">Day to Change History</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-24 px-6 md:px-12 lg:px-24" data-testid="contact-section">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left - Info */}
            <div>
              <span className="text-sm uppercase tracking-widest text-secondary font-medium mb-4 block">
                Start The Conversation
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold tracking-tight mb-6">
                Request Your <span className="text-gradient-quantum">Partnership Deck</span>
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Each partnership is custom-crafted. Share your interests and our team will prepare 
                a tailored proposal demonstrating the unique value we can create together.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <a href="mailto:contact@globalintech.ai" className="font-medium hover:text-primary transition-colors">
                      contact@globalintech.ai
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Venue</p>
                    <p className="font-medium">Tsai Auditorium, Harvard University</p>
                  </div>
                </div>
              </div>

              <Link
                to="/vernand-morency"
                className="inline-flex items-center gap-2 text-primary hover:underline font-medium"
              >
                Learn about the Chair, Vernand Morency
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>

            {/* Right - Form */}
            <div className="glass-card rounded-2xl p-8">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="font-outfit text-xl font-bold mb-2">Request Received</h3>
                  <p className="text-muted-foreground">
                    Your email client should have opened. If not, please email us directly at contact@globalintech.ai
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="Your name"
                      data-testid="partner-name-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="organization" className="block text-sm font-medium mb-2">Organization *</label>
                    <input
                      type="text"
                      id="organization"
                      required
                      value={formData.organization}
                      onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="Company or organization name"
                      data-testid="partner-org-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      id="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="you@organization.com"
                      data-testid="partner-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium mb-2">Phone (optional)</label>
                    <input
                      type="tel"
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                      placeholder="+1 (555) 000-0000"
                      data-testid="partner-phone-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">What interests you most about this partnership?</label>
                    <textarea
                      id="message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors resize-none"
                      placeholder="Tell us about your organization and partnership interests..."
                      data-testid="partner-message-input"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-primary-foreground rounded-xl font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50"
                    data-testid="submit-partnership-btn"
                  >
                    {isSubmitting ? 'Preparing...' : 'Request Partnership Deck'}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="glass-card rounded-3xl p-12 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-secondary/10" />
            
            <div className="relative z-10">
              <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-4">
                Tradition Meets the Future
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
                April 17th, 2026. Harvard University. Will your organization be part of the movement 
                that solves global housing?
              </p>
              <a
                href="mailto:vernand@globalintech.ai?subject=Let's Discuss Partnership - Quantum Housing Conference"
                className="inline-flex items-center gap-2 px-10 py-5 bg-primary text-primary-foreground rounded-full font-semibold text-lg transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(var(--primary-rgb),0.5)]"
                data-testid="final-cta-btn"
              >
                Contact the Chair Directly
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SponsorshipPage;
